import { useMutation } from '@tanstack/react-query'
import { type FormEvent, type RefObject, useId, useRef, useState } from 'react'
import { ApiFailure, codeOf, fieldErrorsOf } from '../lib/api-error.ts'
import { changePlatformPassword } from '../lib/gateway.ts'
import { messageFor } from '../lib/messages.ts'
import { Notice } from '../shell/notice.tsx'
import { Card } from '../tenants/card.tsx'

/**
 * The Password card — docs/ux/PLATFORM-ADMIN.md §4.4 ("Your account"), §19 and §20.2.
 *
 * Three fields: the current password, a new one and the new one again. It changes **this**
 * administrator's password and nobody else's (`POST /platform/auth/password` takes no id), and the
 * sentence that matters is on screen **before** the submit: changing it ends every other session of
 * the account, and this one stays signed in.
 *
 * **What the form decides, and what the API does.** The browser checks the confirmation (it is never
 * sent) and the 12 to 256 bound (so a value the API would refuse never leaves the form); the API
 * checks the current password. A wrong one answers **400 `password_incorrect`**, not a 401, and so
 * lands on the current-password field without the transport refreshing, replaying or ending the
 * session over a typo (§20.2). That field is emptied and takes focus; the new password and its
 * confirmation are kept, since nothing was wrong with them.
 *
 * **One validation vocabulary** (the create form's, TAS-17): the browser's own bubble is off
 * (`noValidate`), the submit is *not* held inert while the form is incomplete, and pressing it with
 * something wrong writes the same inline sentence under each field that needs one and puts focus in
 * the first. The sentences are live from then on: each goes the moment its field is valid.
 *
 * **Inert while in flight, once.** The button is disabled for the whole request, and the handler
 * itself refuses a second submission (Enter in a field does not go through the button).
 *
 * **What is never kept** (**HB4**): neither password is echoed, logged, put in a message, written to
 * browser storage, or left in the query client. The mutation is dropped from its cache the moment it
 * settles (`gcTime: 0` and `reset()`), because a settled mutation otherwise keeps the variables it was
 * called with for five minutes. On success all three fields are emptied.
 *
 * **A 401 here is the session ending**, by one of three paths, each pinned by a test:
 *   1. the access token had merely expired: the transport refreshes and replays, the replay answers
 *      204, and the owner never notices (the success notice shows);
 *   2. the refresh is refused: the session store ends the session inside its own rotate. This is the
 *      realistic case of a change made from another session of the account winning, since that change
 *      revokes this session's refresh token too (`auth_token_invalid`);
 *   3. the refresh succeeds and the replay is refused again: the failure reaches the mutation cache's
 *      `onFailure` (`lib/runtime.ts`), and `PlatformSessionStore.judgeUnauthorized` ends the session.
 * In 2 and 3 the sign-in page says the session ended; none is shown as a wrong password.
 */

/** The API's bounds on `newPassword` (the bootstrap's own floor) and on both fields' ceiling. */
export const PASSWORD_MIN_LENGTH = 12
export const PASSWORD_MAX_LENGTH = 256

type Field = 'currentPassword' | 'newPassword' | 'confirmPassword'

/** The API's `details[].path` (prefix stripped) for each field it can name. `confirm` is never sent. */
const FIELD_FOR_PATH: Readonly<Record<string, Field>> = {
  currentPassword: 'currentPassword',
  newPassword: 'newPassword',
}

/** One line per field, never the server's own prose (§20.1). Also the server-side fallback copy. */
function copyForField(field: Field): string {
  switch (field) {
    case 'currentPassword':
      return 'Enter your current password — at most 256 characters.'
    case 'newPassword':
      return 'Use 12 to 256 characters.'
    case 'confirmPassword':
      return 'Type the new password again, exactly the same.'
  }
}

/** The inline sentence a value earns before it is sent, or `undefined` when it is acceptable. */
function problemWith(field: Field, value: string, newPassword: string): string | undefined {
  switch (field) {
    case 'currentPassword':
      return value.length === 0 || value.length > PASSWORD_MAX_LENGTH
        ? copyForField(field)
        : undefined
    case 'newPassword':
      if (value.length < PASSWORD_MIN_LENGTH) {
        return 'Use at least 12 characters.'
      }
      return value.length > PASSWORD_MAX_LENGTH ? 'Use at most 256 characters.' : undefined
    case 'confirmPassword':
      // Empty is a mismatch even when the new password is empty too: nothing was confirmed.
      return value.length > 0 && value === newPassword ? undefined : copyForField(field)
  }
}

const FIELD_ORDER: readonly Field[] = ['currentPassword', 'newPassword', 'confirmPassword']

export function PasswordCard({ onChanged }: { readonly onChanged: () => void }) {
  const consequenceId = useId()
  const currentId = useId()
  const currentErrorId = useId()
  const newId = useId()
  const newHelpId = useId()
  const confirmId = useId()
  const confirmErrorId = useId()
  const fieldRefs: Readonly<Record<Field, RefObject<HTMLInputElement | null>>> = {
    currentPassword: useRef<HTMLInputElement>(null),
    newPassword: useRef<HTMLInputElement>(null),
    confirmPassword: useRef<HTMLInputElement>(null),
  }

  const [values, setValues] = useState<Record<Field, string>>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })
  /** Whether Change password has been pressed with something wrong; from then on the sentences are live. */
  const [attempted, setAttempted] = useState(false)
  /** What the API refused, by field — cleared by the first edit of that field. */
  const [serverErrors, setServerErrors] = useState<Partial<Record<Field, string>>>({})
  /** A refusal no field owns: the code, resolved to a sentence at render. */
  const [failureCode, setFailureCode] = useState<string | undefined>(undefined)
  /** The success notice, until the first edit made after it appears (as Settings and Branding's "Saved."). */
  const [changed, setChanged] = useState(false)

  const change = useMutation({
    mutationFn: changePlatformPassword,
    // Do not keep the variables (both passwords) in the mutation cache after this settles.
    gcTime: 0,
  })

  const errorOf = (field: Field): string | undefined =>
    serverErrors[field] ??
    (attempted ? problemWith(field, values[field], values.newPassword) : undefined)

  const edit = (field: Field, value: string) => {
    setValues((current) => ({ ...current, [field]: value }))
    setServerErrors(({ [field]: _gone, ...rest }) => rest)
    setFailureCode(undefined)
    setChanged(false)
  }

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    // The button is disabled while the request is out, but Enter in a field does not go through it.
    if (change.isPending) {
      return
    }
    setServerErrors({})
    setFailureCode(undefined)
    setChanged(false)

    const first = FIELD_ORDER.find(
      (field) => problemWith(field, values[field], values.newPassword) !== undefined,
    )
    if (first !== undefined) {
      // Nothing is sent: the API would refuse it, or the confirmation is the browser's alone.
      setAttempted(true)
      fieldRefs[first].current?.focus()
      return
    }

    change.mutate(
      { currentPassword: values.currentPassword, newPassword: values.newPassword },
      {
        onSuccess: () => {
          setValues({ currentPassword: '', newPassword: '', confirmPassword: '' })
          setAttempted(false)
          setChanged(true)
          onChanged()
        },
        onError: (thrown) => {
          const code = codeOf(thrown)
          if (code === 'password_incorrect') {
            // The 400 that must not end the session. The wrong value is not kept.
            setServerErrors({ currentPassword: messageFor('account', code) })
            setValues((current) => ({ ...current, currentPassword: '' }))
            fieldRefs.currentPassword.current?.focus()
            return
          }
          if (code === 'validation_failed' && thrown instanceof ApiFailure) {
            const named = fieldErrorsOf<Field>(thrown, FIELD_FOR_PATH, copyForField)
            const firstNamed = FIELD_ORDER.find((field) => named[field] !== undefined)
            if (firstNamed !== undefined) {
              setServerErrors(named)
              fieldRefs[firstNamed].current?.focus()
              return
            }
          }
          setFailureCode(code)
        },
        // After the callbacks above, which have already taken what they need from the error.
        onSettled: () => {
          change.reset()
        },
      },
    )
  }

  const currentError = errorOf('currentPassword')
  const newError = errorOf('newPassword')
  const confirmError = errorOf('confirmPassword')

  return (
    <Card title="Password">
      <form className="card-body form" onSubmit={submit} noValidate>
        {changed ? (
          <Notice tone="success">
            Your password has been changed. Every other session of this account has ended; this one
            stays signed in.
          </Notice>
        ) : null}
        {failureCode === undefined ? null : (
          <Notice tone="failure" assertive>
            {messageFor('account', failureCode)}
          </Notice>
        )}

        <p className="small secondary" id={consequenceId}>
          Changing your password ends every other session signed in to this account — in any other
          tab, browser or device. This session stays signed in.
        </p>

        <div className="field">
          <label htmlFor={currentId}>Current password</label>
          <input
            id={currentId}
            ref={fieldRefs.currentPassword}
            type="password"
            autoComplete="current-password"
            required
            value={values.currentPassword}
            aria-invalid={currentError !== undefined}
            {...(currentError === undefined ? {} : { 'aria-describedby': currentErrorId })}
            onChange={(event) => {
              edit('currentPassword', event.target.value)
            }}
          />
          {currentError === undefined ? null : (
            <p className="field-error" id={currentErrorId}>
              {currentError}
            </p>
          )}
        </div>

        <div className="field">
          <label htmlFor={newId}>New password</label>
          <input
            id={newId}
            ref={fieldRefs.newPassword}
            type="password"
            autoComplete="new-password"
            required
            value={values.newPassword}
            aria-invalid={newError !== undefined}
            aria-describedby={newHelpId}
            onChange={(event) => {
              edit('newPassword', event.target.value)
            }}
          />
          <div id={newHelpId}>
            {newError === undefined ? null : <p className="field-error">{newError}</p>}
            <p className="muted">At least 12 characters. Spaces count and are kept.</p>
          </div>
        </div>

        <div className="field">
          <label htmlFor={confirmId}>Confirm new password</label>
          <input
            id={confirmId}
            ref={fieldRefs.confirmPassword}
            type="password"
            autoComplete="new-password"
            required
            value={values.confirmPassword}
            aria-invalid={confirmError !== undefined}
            {...(confirmError === undefined ? {} : { 'aria-describedby': confirmErrorId })}
            onChange={(event) => {
              edit('confirmPassword', event.target.value)
            }}
          />
          {confirmError === undefined ? null : (
            <p className="field-error" id={confirmErrorId}>
              {confirmError}
            </p>
          )}
        </div>

        <div>
          <button
            type="submit"
            className="button button-primary"
            disabled={change.isPending}
            aria-busy={change.isPending}
            aria-describedby={consequenceId}
          >
            {change.isPending ? 'Changing…' : 'Change password'}
          </button>
        </div>
      </form>
    </Card>
  )
}
